import { useEffect, useMemo } from 'react';
import type {
  Control,
  FieldValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import type { Option } from '@/components/inputs/AutocompleteInput';

import {
  buildCommerceSelectOptions,
  buildSubOperatorSelectOptions,
  useCommerceScopeSearch,
  useIncludeAllSync,
  useInviteUserOptions,
} from '../hooks';
import { RoleRestriction } from '../enums';
import { useGetAllBranchesQuery } from '../../Commerce/services/branches.service';
import { BranchStatus } from '../../Commerce/models/Branch';
import {
  OperatorAccessCard,
  CommerceAccessCard,
  ExternalOperatorAccessCard,
} from './UserAccessScope';
import type { ExternalOperatorKind } from './UserAccessScope/ExternalOperatorAccessCard';

function watchScopeBoolean(watch: UseFormWatch<any>, path: string): boolean {
  return watch(path) === true;
}

function watchScopeStringArray(
  watch: UseFormWatch<any>,
  path: string
): string[] {
  const v = watch(path);
  return Array.isArray(v) ? (v as string[]) : [];
}

type UserAccessScopeSectionProps = {
  control: Control<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  userType?: string;
  operatorsError?: string;
  branchesError?: string;
};


const UserAccessScopeSection = ({
  control,
  watch,
  setValue,
  userType,
  operatorsError,
  branchesError,
}: UserAccessScopeSectionProps) => {
  const isCommerceUser = userType === RoleRestriction.COMMERCE;
  const isExternalOperatorUser = userType === RoleRestriction.EXTERNAL_OPERATOR;

  const operatorsIncludeAll = watchScopeBoolean(watch, 'operators.includeAll');
  const commercesIncludeAll = watchScopeBoolean(watch, 'commerces.includeAll');
  const branchesIncludeAll = watchScopeBoolean(watch, 'branches.includeAll');
  const subOperatorsIncludeAll = watchScopeBoolean(
    watch,
    'operators.subOperators.includeAll'
  );
  const selectedCommerces = watchScopeStringArray(watch, 'commerces.selected');
  const excludedCommerces = watchScopeStringArray(watch, 'commerces.excluded');
  const selectedOperators = watchScopeStringArray(watch, 'operators.selected');
  const selectedSubOperators = watchScopeStringArray(
    watch,
    'operators.subOperators.selected'
  );
  const excludedSubOperators = watchScopeStringArray(
    watch,
    'operators.subOperators.excluded'
  );
  const selectedBranches = watchScopeStringArray(watch, 'branches.selected');
  const excludedBranches = watchScopeStringArray(watch, 'branches.excluded');

  const branchesRequiredForSubOperator = watchScopeBoolean(
    watch,
    'branches.requiredForSubOperator'
  );

  const {
    mainOperatorsOptions,
    externalOperatorsOptions,
    externalOperatorsCatalog,
    branchesData,
    isLoading: isLoadingOptions,
    operatorsCatalog,
    associationsCatalog,
    commercesCatalog,
  } = useInviteUserOptions({
    userType,
  });

  const selectedExternalOperatorId = isExternalOperatorUser
    ? selectedOperators[0] ?? ''
    : '';

  const selectedExternalOperator = useMemo(
    () =>
      externalOperatorsCatalog.find(
        (operator) => operator.id === selectedExternalOperatorId
      ) ?? null,
    [externalOperatorsCatalog, selectedExternalOperatorId]
  );

  const externalOperatorKind: ExternalOperatorKind | null =
    selectedExternalOperator
      ? selectedExternalOperator.parentId
        ? 'subOperator'
        : 'headquarter'
      : null;
  const isExternalSubOperator = externalOperatorKind === 'subOperator';

  const parentOperatorName = useMemo(() => {
    const parentId = selectedExternalOperator?.parentId;
    if (!parentId) return undefined;
    return externalOperatorsCatalog.find((operator) => operator.id === parentId)
      ?.fullName;
  }, [externalOperatorsCatalog, selectedExternalOperator]);

  const externalOperatorCommerceIds = useMemo(() => {
    if (!isExternalSubOperator) return [];
    return [
      ...new Set(
        associationsCatalog
          .filter(
            (association) =>
              association.logisticOperatorId === selectedExternalOperatorId
          )
          .map((association) => association.commerceId)
      ),
    ];
  }, [isExternalSubOperator, associationsCatalog, selectedExternalOperatorId]);

  const {
    currentData: externalOperatorBranchesData,
    isFetching: isFetchingExternalOperatorBranches,
  } = useGetAllBranchesQuery(
    {
      paginated: { page: 1, pageSize: 1000 },
      filters: { commerceIds: externalOperatorCommerceIds, enabled: true },
    },
    { skip: externalOperatorCommerceIds.length === 0 }
  );

  const externalOperatorBranches = useMemo(() => {
    if (externalOperatorCommerceIds.length === 0) return [];
    return (externalOperatorBranchesData?.items ?? []).filter(
      (branch) => branch.status !== BranchStatus.INACTIVE
    );
  }, [externalOperatorCommerceIds, externalOperatorBranchesData]);

  const externalOperatorBranchOptions: Option[] = useMemo(() => {
    const showCommerceName = externalOperatorCommerceIds.length > 1;
    return externalOperatorBranches.map((branch) => {
      const commerceName = commercesCatalog.find(
        (commerce) => commerce.id === branch.commerceId
      )?.name;
      return {
        label:
          showCommerceName && commerceName
            ? `${commerceName} · ${branch.name}`
            : branch.name,
        data: branch.id,
      };
    });
  }, [externalOperatorCommerceIds, externalOperatorBranches, commercesCatalog]);

  useEffect(() => {
    if (!isExternalOperatorUser || !selectedExternalOperator) return;
    if (branchesRequiredForSubOperator !== isExternalSubOperator) {
      setValue('branches.requiredForSubOperator', isExternalSubOperator, {
        shouldValidate: true,
      });
    }
  }, [
    isExternalOperatorUser,
    selectedExternalOperator,
    isExternalSubOperator,
    branchesRequiredForSubOperator,
    setValue,
  ]);

  const handleExternalOperatorChange = (value: string) => {
    setValue('operators.selected', value ? [value] : [], {
      shouldValidate: true,
    });
    const nextOperator = externalOperatorsCatalog.find(
      (operator) => operator.id === value
    );
    setValue(
      'branches',
      {
        includeAll: false,
        selected: [],
        excluded: [],
        requiredForSubOperator: !!nextOperator?.parentId,
      },
      { shouldValidate: true }
    );
  };

  const handleExternalBranchChange = (value: string) => {
    setValue('branches.selected', value ? [value] : [], {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const commerceSearch = useCommerceScopeSearch({
    enabled: isCommerceUser,
    selectedCommerceIds: selectedCommerces,
    excludedCommerceIds: excludedCommerces,
  });

  useEffect(() => {
    if (!operatorsIncludeAll && selectedOperators.length === 0) {
      setValue('operators.subOperators', {
        includeAll: false,
        selected: [],
        excluded: [],
      });
    }
  }, [operatorsIncludeAll, selectedOperators.length, setValue]);

  const subOperatorsOptions = useMemo(
    () =>
      buildSubOperatorSelectOptions(operatorsCatalog, selectedOperators),
    [operatorsCatalog, selectedOperators]
  );

  const includedSubOperatorIds = useMemo(() => {
    if (!subOperatorsIncludeAll) {
      return selectedSubOperators;
    }
    return subOperatorsOptions
      .filter((op) => !excludedSubOperators.includes(op.value))
      .map((op) => op.value);
  }, [
    subOperatorsIncludeAll,
    selectedSubOperators,
    excludedSubOperators,
    subOperatorsOptions,
  ]);

  const allIncludedOperatorIds = useMemo(() => {
    return [...selectedOperators, ...includedSubOperatorIds];
  }, [selectedOperators, includedSubOperatorIds]);

  const commercesOptions = useMemo(
    () =>
      buildCommerceSelectOptions(
        commercesCatalog,
        associationsCatalog,
        allIncludedOperatorIds
      ),
    [commercesCatalog, associationsCatalog, allIncludedOperatorIds]
  );

  const includedCommerceIds = useMemo(() => {
    if (isCommerceUser || !commercesIncludeAll) {
      return selectedCommerces;
    }
    return commercesOptions
      .filter((commerce) => !excludedCommerces.includes(commerce.value))
      .map((commerce) => commerce.value);
  }, [
    isCommerceUser,
    commercesIncludeAll,
    selectedCommerces,
    excludedCommerces,
    commercesOptions,
  ]);

  useEffect(() => {
    if (isExternalOperatorUser) return;
    if (includedCommerceIds.length === 0) {
      setValue('branches', {
        includeAll: false,
        selected: [],
        excluded: [],
      });
    }
  }, [isExternalOperatorUser, includedCommerceIds.length, setValue]);

  const { getBranchCommerceId, allBranchIds } = commerceSearch;

  useEffect(() => {
    if (!isCommerceUser || selectedBranches.length === 0) return;
    const branchesOfSelectedCommerces = selectedBranches.filter((branchId) => {
      const commerceId = getBranchCommerceId(branchId);
      return !commerceId || selectedCommerces.includes(commerceId);
    });
    if (branchesOfSelectedCommerces.length !== selectedBranches.length) {
      setValue('branches.selected', branchesOfSelectedCommerces, {
        shouldValidate: true,
      });
    }
  }, [
    isCommerceUser,
    selectedBranches,
    selectedCommerces,
    getBranchCommerceId,
    setValue,
  ]);

  const selectAllBranches = () => {
    if (!allBranchIds) return;
    setValue(
      'branches.selected',
      [...new Set([...selectedBranches, ...allBranchIds])],
      { shouldValidate: true, shouldDirty: true }
    );
  };

  const clearBranches = () => {
    setValue('branches.selected', [], {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const areAllBranchesSelected = useMemo(() => {
    if (!allBranchIds || allBranchIds.length === 0) return false;
    const selected = new Set(selectedBranches);
    return allBranchIds.every((branchId) => selected.has(branchId));
  }, [allBranchIds, selectedBranches]);

  const branchesOptions = useMemo(() => {
    if (!branchesData || branchesData.length === 0) {
      return [];
    }

    if (includedCommerceIds.length === 0) {
      return [];
    }

    return branchesData
      .filter((branch) => includedCommerceIds.includes(branch.commerceId))
      .map((branch) => ({
        label: branch.name,
        value: branch.id,
      }));
  }, [branchesData, includedCommerceIds]);

  const setValueForSync = setValue as UseFormSetValue<FieldValues>;

  useIncludeAllSync({
    includeAll: subOperatorsIncludeAll,
    selected: selectedSubOperators,
    excluded: excludedSubOperators,
    selectedPath: 'operators.subOperators.selected',
    excludedPath: 'operators.subOperators.excluded',
    setValue: setValueForSync,
  });

  useIncludeAllSync({
    includeAll: commercesIncludeAll,
    selected: selectedCommerces,
    excluded: excludedCommerces,
    selectedPath: 'commerces.selected',
    excludedPath: 'commerces.excluded',
    setValue: setValueForSync,
  });

  useIncludeAllSync({
    includeAll: branchesIncludeAll,
    selected: selectedBranches,
    excluded: excludedBranches,
    selectedPath: 'branches.selected',
    excludedPath: 'branches.excluded',
    setValue: setValueForSync,
  });

  const operatorsOptions: Option[] = useMemo(
    () => mainOperatorsOptions.map((o) => ({ label: o.label, data: o.value })),
    [mainOperatorsOptions]
  );
  const subOperatorsOptionsAsOption: Option[] = useMemo(
    () => subOperatorsOptions.map((o) => ({ label: o.label, data: o.value })),
    [subOperatorsOptions]
  );
  const commercesOptionsAsOption: Option[] = useMemo(
    () => commercesOptions.map((o) => ({ label: o.label, data: o.value })),
    [commercesOptions]
  );
  const branchesOptionsAsOption: Option[] = useMemo(
    () => branchesOptions.map((o) => ({ label: o.label, data: o.value })),
    [branchesOptions]
  );

  return (
    <div className='flex gap-6'>
      {isExternalOperatorUser && (
        <ExternalOperatorAccessCard
          isLoadingOptions={isLoadingOptions}
          operatorsOptions={externalOperatorsOptions}
          value={selectedOperators[0] ?? ''}
          onChange={handleExternalOperatorChange}
          error={operatorsError}
          operatorKind={externalOperatorKind}
          parentOperatorName={parentOperatorName}
          branchOptions={externalOperatorBranchOptions}
          branchValue={selectedBranches[0] ?? ''}
          onBranchChange={handleExternalBranchChange}
          isLoadingBranches={
            isLoadingOptions || isFetchingExternalOperatorBranches
          }
          branchError={branchesError}
        />
      )}

      {!isCommerceUser && !isExternalOperatorUser && (
        <OperatorAccessCard
          control={control}
          isLoadingOptions={isLoadingOptions}
          operatorsOptions={operatorsOptions}
          subOperatorsOptions={subOperatorsOptionsAsOption}
          hasSelectedOperators={
            selectedOperators.length > 0 || operatorsIncludeAll
          }
          subOperatorsIncludeAll={subOperatorsIncludeAll}
        />
      )}

      {isCommerceUser && (
        <CommerceAccessCard
          control={control}
          isLoadingOptions={isLoadingOptions}
          commercesOptions={
            isCommerceUser ? commerceSearch.options : commercesOptionsAsOption
          }
          commercesIncludeAll={commercesIncludeAll}
          isCommerceRequired={isCommerceUser}
          hasIncludedCommerces={includedCommerceIds.length > 0}
          branchesOptions={
            isCommerceUser
              ? commerceSearch.branchesOptions
              : branchesOptionsAsOption
          }
          branchesIncludeAll={branchesIncludeAll}
          showExcludeBranchesToggle={!isCommerceUser}
          bulkBranches={{
            total: allBranchIds?.length ?? 0,
            selectedCount: selectedBranches.length,
            areAllSelected: areAllBranchesSelected,
            isReady: !!allBranchIds && !branchesIncludeAll,
            onSelectAll: selectAllBranches,
            onClear: clearBranches,
          }}
          showExcludeCommercesToggle={!isCommerceUser}
          search={
            isCommerceUser
              ? {
                  inputValue: commerceSearch.inputValue,
                  onInputChange: commerceSearch.setInputValue,
                  labelsById: commerceSearch.labelsById,
                  isSearching: commerceSearch.isSearching,
                  isLoadingBranches: commerceSearch.isLoadingBranches,
                }
              : undefined
          }
        />
      )}
    </div>
  );
};

export default UserAccessScopeSection;
